import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Modal } from 'antd';
import AlertMessage from 'components/alertMessage/alert-message';
import { Layout, List, TransferBar } from 'components/Page';
import {
  add, clear, load, remove
} from 'actions';

/**
 * @description Map data and server flight
 * @param {state} Object
 */
const mapStateToProps = (state, ownProps) => ({
  destination: {
    results: state.api[ownProps.reducer.destination.key].data.results,
    totalRecords: state.api[ownProps.reducer.destination.key].data.count,
    inFlight: state.api[ownProps.reducer.destination.key].inFlight,
    isDirty: state.api[ownProps.reducer.destination.key].isDirty
  },
  source: {
    results: state.api[ownProps.reducer.source.key].data.results,
    totalRecords: state.api[ownProps.reducer.source.key].data.count,
    inFlight: state.api[ownProps.reducer.source.key].inFlight,
    isDirty: state.api[ownProps.reducer.source.key].isDirty
  },
  error: { ...state.api[ownProps.reducer.source.key].error, ...state.api[ownProps.reducer.destination.key].error }
});
/**
 * @description Dispatch create, loadList, load, deleteItem
 * @param {dispatch} Object
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  loadDestination(params) {
    return dispatch(load(ownProps.reducer.destination.key, params));
  },
  loadSource(params) {
    return dispatch(load(ownProps.reducer.source.key, params));
  },
  assign(data) {
    return dispatch(add(ownProps.reducer.destination.key, {
      id: ownProps.objectId,
      params: data
    }));
  },
  unassign(data) {
    return dispatch(remove(ownProps.reducer.destination.key, {
      id: ownProps.objectId,
      params: data
    }));
  },
  clearErrors() {
    dispatch(clear(ownProps.reducer.destination.key));
    dispatch(clear(ownProps.reducer.source.key));
  }
});

/**
 * LeaveTypes
 * @description Leave Type View
 * @version 1.0.0
 */
class Transfer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showInterceptor: false,
      sourceKeys: [],
      destinationKeys: [],
      showError: false
    };

    this.getSourceRef = this.getSourceRef.bind(this);
    this.getDestinationRef = this.getDestinationRef.bind(this);
    this.handleAssign = this.handleAssign.bind(this);
    this.handleUnassign = this.handleUnassign.bind(this);
    this.handleAddClick = this.handleAddClick.bind(this);

    this.handleModalOk = this.handleModalOk.bind(this);
    this.handleModalCancel = this.handleModalCancel.bind(this);

    this.handleSourceRowClick = this.handleSourceRowClick.bind(this);
    this.handleDestinationRowClick = this.handleDestinationRowClick.bind(this);

    this.handlePopupOk = this.handlePopupOk.bind(this);
  }

  shouldComponentUpdate(nextProps) {
    const { error } = this.props;
    // check to see if there is an error coming
    if (!_.isEqual(nextProps.error, error)) {
      // show the initial error
      this.setState({
        showError: true
      });
    }
    // error has already been shown so don't show new errors
    if (nextProps.error.allErrors.length || error.allErrors.length) {
      this.setState({
        showError: false
      });
    }
    return true;
  }

  /**
   * @description get Source List reference
   * @param {form} Object
   */
  getSourceRef(component) {
    this.sourceComponent = this.sourceComponent || component;
  }

  /**
   * @description get Destination List reference
   * @param {form} Object
   */
  getDestinationRef(component) {
    this.destinationComponent = this.destinationComponent || component;
  }

  /**
   * @description Get Source List of data
   * @returns {List}
   */
  getSource() {
    const {
      canAdmin, config, source, loadSource
    } = this.props;
    const { sourceKeys, destinationKeys } = this.state;
    return (
      <List
        canAdmin={canAdmin}
        className="bhp-container bhp-container-footer"
        // default button config
        commands={{
          showAdd: false,
          showDelete: false
        }}
        dataSource={source.results}
        footer={(
          <TransferBar
            // are there selected rows or is there config
            commands={{
              disableAdd: config.commands.disableAdd || !sourceKeys.length,
              disableRemove: config.commands.disableRemove || !destinationKeys.length
            }}
            onAddClick={this.handleAddClick}
            onRemoveClick={this.handleUnassign}
          />
        )}
        inFlight={source.inFlight}
        onLoadList={loadSource}
        onRowClick={this.handleSourceRowClick}
        onRowSelectionChange={this.handleSourceRowClick}
        totalRecords={source.totalRecords}
        {...config.source}
        ref={this.getSourceRef}
      />
    );
  }

  /**
   * @description Get Destination List Data
   * @returns {Detail}
   */
  getDestination() {
    const {
      canAdmin, config, destination, loadDestination
    } = this.props;
    return (
      <List
        canAdmin={canAdmin}
        className="bhp-container"
        // default button config
        commands={{
          showAdd: false,
          showDelete: false
        }}
        dataSource={destination.results}
        inFlight={destination.inFlight}
        onLoadList={loadDestination}
        onRowClick={this.handleDestinationRowClick}
        onRowSelectionChange={this.handleDestinationRowClick}
        totalRecords={destination.totalRecords}
        {...config.destination}
        ref={this.getDestinationRef}
      />
    );
  }

  /**
   * @description Get feed back from parent, hide modal and handle assigning
   *              on parent
   */
  handleModalOk() {
    const { onInterceptorOk } = this.props;
    const process = onInterceptorOk();
    if (process) {
      this.setState({
        showInterceptor: false
      }, () => {
        this.handleAssign();
      });
    }
  }

  /**
   * @description Hide the modal and provide control back to parent
   */
  handleModalCancel() {
    const { onInterceptorCancel } = this.props;
    this.setState({
      showInterceptor: false
    }, () => {
      onInterceptorCancel();
    });
  }

  /**
   * @description Do the assigning of records
   */
  handleAssign() {
    const { assign, onPrepareSourceData, onAssignSuccess } = this.props;
    // get the selected keys from source table
    const ids = this.sourceComponent.state.selectedRowKeys;
    // get the formatted data from parent
    const data = onPrepareSourceData(ids);
    // Send data to API and reload the Source and Destination tables if successfull
    assign(data)
      .then((response) => {
        this.destinationComponent.loadList();
        onAssignSuccess?.(response);
      })
      .finally(() => {
        this.sourceComponent.loadList();
        // cleanup selected rows
        this.setState({
          sourceKeys: [],
          destinationKeys: []
        });
      });
  }

  /**
   * @description Check for an inteceptor else handle the assigning
   */
  handleAddClick() {
    const { interceptor } = this.props;
    // if there is an inteceptor, show it else handle assigning
    if (!_.isEmpty(interceptor)) {
      this.setState({ showInterceptor: true });
    }
    else {
      this.handleAssign();
    }
  }

  /**
   * @description Unassign selected rows
   */
  handleUnassign() {
    const { unassign, onPrepareDestinationData, onUnassignSuccess } = this.props;
    // get the selected rows from the destinatio Table
    const ids = this.destinationComponent.state.selectedRowKeys;
    // Get formatted data from parent
    const data = onPrepareDestinationData(ids);
    // Send data to API and reload the Source and Destination tables if successfull
    unassign(data)
      .then((response) => {
        this.sourceComponent.loadList();
        onUnassignSuccess?.(response);
      })
      .finally(() => {
        this.destinationComponent.loadList();
        // clean up tables
        this.setState({
          sourceKeys: [],
          destinationKeys: []
        }, () => {
          this.destinationComponent.setState({
            selectedRowKeys: []
          });
          this.sourceComponent.setState({
            selectedRowKeys: []
          });
        });
      });
  }

  /**
   * @description Update the selected Source row keys
   */
  handleSourceRowClick(selectedRowKeys) {
    this.setState({
      sourceKeys: selectedRowKeys
    });
  }

  /**
   * @description Update the selected Destination row keys
   */
  handleDestinationRowClick(selectedRowKeys) {
    this.setState({
      destinationKeys: selectedRowKeys
    });
  }

  handlePopupOk() {
    const { clearErrors } = this.props;
    const { destinationComponent, sourceComponent } = this;
    this.setState({
      sourceKeys: [],
      destinationKeys: []
    }, () => {
      destinationComponent.clearSelectedRows();
      sourceComponent.clearSelectedRows();
      clearErrors();
    });
  }

  render() {
    const { classes, error, interceptor } = this.props;
    const { showError, showInterceptor } = this.state;
    // set the errors for popup
    const errorList = error.allErrors.length ? error.allErrors : [];
    // Layout Component to build out the Page
    return (
      <div className="bhp-container">
        <Layout
          classes={classes}
          component1={this.getSource()}
          component2={this.getDestination()}
          split="horizontal"
        />
        <Modal
          visible={showInterceptor}
          width={interceptor.width}
          title={interceptor.title}
          wrapClassName="bhp-modal-form bhp-vertical-centered"
          closable={false}
          destroyOnClose
          okText="Add"
          onOk={this.handleModalOk}
          onCancel={this.handleModalCancel}
        >
          {interceptor.component && interceptor.component()}
        </Modal>
        {showError && (
          <AlertMessage
            popUp
            size="small"
            type="error"
            title="Error"
            summary="The following errors occured:"
            messageList={errorList}
            onPopUpOk={this.handlePopupOk}
          />
        )}
      </div>
    );
  }
}

Transfer.propTypes = {
  assign: PropTypes.func,
  canAdmin: PropTypes.bool,
  classes: PropTypes.string,
  clearErrors: PropTypes.func,
  config: PropTypes.object,
  destination: PropTypes.object,
  error: PropTypes.object,
  interceptor: PropTypes.object,
  loadDestination: PropTypes.func,
  loadSource: PropTypes.func,
  objectId: PropTypes.number,
  onInterceptorOk: PropTypes.func,
  onInterceptorCancel: PropTypes.func,
  onPrepareSourceData: PropTypes.func,
  onPrepareDestinationData: PropTypes.func,
  onAssignSuccess: PropTypes.func,
  onUnassignSuccess: PropTypes.func,
  source: PropTypes.object,
  unassign: PropTypes.func
};

Transfer.defaultProps = {
  assign() {
  },
  canAdmin: false,
  classes: '',
  clearErrors() {
  },
  config: {},
  destination: {
    inFlight: false,
    isDirty: false,
    results: [],
    totalRecords: null
  },
  error: {
    allErrors: []
  },
  interceptor: {},
  loadDestination() {
  },
  loadSource() {
  },
  objectId: null,
  onInterceptorOk: () => true,
  onInterceptorCancel() {
  },
  onPrepareSourceData: data => data,
  onPrepareDestinationData: data => data,
  onAssignSuccess: null,
  onUnassignSuccess: null,
  source: {
    inFlight: false,
    isDirty: false,
    results: [],
    totalRecords: null
  },
  unassign() {
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(Transfer);
